import type { NestedKeys } from '../../types.ts';

/**
 * `en` · `surface` namespace — chrome for the companion surface (the PWA shell):
 * stream states, artifact viewer chrome, modal surfaces, realtime pushes, and
 * the service-worker update prompt.
 *
 * These lived as an app-local tree in `apps/frontend/src/i18n/messages.ts`,
 * which put 44 English literals outside the i18n system entirely — untranslatable
 * and invisible to the split-bundle loader. They belong here.
 *
 * Keys that already had a shared home are deliberately NOT re-declared:
 *
 * | app-local key         | use instead                 |
 * |-----------------------|-----------------------------|
 * | `surface.retry`       | `common.actions.retry`      |
 * | `surface.download`    | `gallery.actions.download`  |
 * | `surface.noMedia`     | `gallery.empty`             |
 */
export const surface = {
  you: 'You',
  companionAvatar: "Your companion's avatar",
  artifacts: 'Artifacts',
  generatingImage: 'Generating image…',
  generatingVideo: 'Generating video…',
  revealedMedia: 'Generated media',
  working: 'Working…',
  streamError: 'Something went wrong. Tap to retry.',
  // A `409`: the edge runs one turn per conversation at a time, so this is "wait a
  // moment", not "something broke" — the retry control below it is the same one.
  conversationBusy: 'She’s still replying. Tap to retry in a moment.',
  emptyThread: 'Say hi to {companionName}.',
  // The edge caps one thread read at its most recent turns and SAYS SO. Rendering
  // that page as if it were the whole conversation would put the silence back one
  // layer down — the user would scroll to the top and read the wrong "beginning".
  historyTruncated: 'Earlier messages aren’t shown here. Your full history is kept.',
  loadingCompanions: 'Loading your companions…',
  required: 'Required',
  formIncomplete: 'Please complete the required fields.',
  confirm: 'Confirm',
  close: 'Close',
  dismiss: 'Dismiss',
  openSurface: 'Open {surface}',
  viewGallery: 'View gallery',
  chooseFile: 'Choose an image',
  fileHint: 'Your own character concept only.',
  // Chrome CTAs
  newCompanion: 'New companion',
  createFirst: 'Create your first companion',
  // Artifacts lightbox actions + outcomes
  setAsAvatar: 'Set as avatar',
  prev: 'Previous',
  next: 'Next',
  galleryError: "Couldn't load the gallery.",
  avatarUpdated: 'Avatar updated.',
  avatarFailed: "Couldn't update the avatar.",
  // Builder outcomes
  companionReady: '{companionName} is on the way.',
  companionFailed: "Couldn't create your companion.",
  // Realtime pushes
  creditsUpdated: 'Your credit balance changed.',
  proactiveFrom: 'New message',
  // Modal surfaces not yet shipped
  comingSoon: '{surface} is coming soon.',
  surfaces: {
    settings: 'Settings',
    billing: 'Billing & credits',
    referral: 'Refer & earn',
  },
  // PWA service-worker update prompt.
  update: {
    available: 'Update available',
    body: 'A new version is ready.',
    reload: 'Reload',
    failed: "Couldn't apply the update. Please try again.",
  },
} as const;

export type SurfaceMessages = typeof surface;

/** Typo-safe dot-path keys for the `surface` namespace. */
export type SurfaceKey = NestedKeys<SurfaceMessages>;
